import { environment } from 'nsdi-volto-frontend/config/environments';

/**
 * Featured Block Schema
 *
 * Defines the editable fields for the Featured data cards block.
 */

/**
 * Card Display Field Schema
 * Defines a single field to display on cards
 */
const cardDisplayFieldSchema = {
  title: 'Card Field',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['field', 'label'],
    },
  ],
  properties: {
    field: {
      title: 'Field Name',
      description:
        'API field name (e.g., format_category, description, views, fileSize, keywords)',
      type: 'string',
    },
    label: {
      title: 'Display Label',
      description: 'Label shown on the card (e.g., Format, Description)',
      type: 'string',
    },
  },
  required: ['field', 'label'],
};

const featuredItemSchema = {
  title: 'Featured Item',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['title', 'description', 'thumbnail', 'format', 'link'],
    },
    {
      id: 'metadata',
      title: 'Metadata',
      fields: ['tags'],
    },
  ],
  properties: {
    title: {
      title: 'Title',
      description: 'Item title',
      type: 'string',
    },
    description: {
      title: 'Description',
      description: 'Brief description or summary',
      type: 'string',
      widget: 'textarea',
    },
    thumbnail: {
      title: 'Thumbnail URL',
      description: 'URL to the thumbnail image',
      type: 'string',
    },
    format: {
      title: 'Format',
      description: 'Data format (e.g., ArcGIS Online, WMS Layer)',
      type: 'string',
    },
    link: {
      title: 'Link URL',
      description: 'URL to the item details page',
      type: 'string',
    },
    tags: {
      title: 'Tags',
      description: 'Comma-separated tags (e.g., "Disaster | Wildfire")',
      type: 'string',
    },
  },
  required: ['title'],
};

const FeaturedBlockSchema = {
  title: 'Featured',
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['sectionTitle', 'maxItemsToShow'],
    },
    {
      id: 'cardDisplay',
      title: 'Card Display',
      fields: ['columns', 'cardDisplayFields', 'cardBadgeField'],
    },
    {
      id: 'display',
      title: 'Display Options',
      fields: ['showFormat', 'showShowAll'],
    },
    {
      id: 'dataSource',
      title: 'Data Source',
      fields: [
        'fetchDynamic',
        'voyagerBaseUrl',
        'voyagerConfigId',
        'featuredQuery',
        'searchResultsBaseUrl',
        'detailsPageBaseUrl',
      ],
    },
    {
      id: 'items',
      title: 'Static Items (when not using dynamic)',
      fields: ['items'],
    },
  ],
  properties: {
    sectionTitle: {
      title: 'Section Title',
      description: 'Heading displayed above the featured items',
      type: 'string',
      default: 'Featured',
    },
    columns: {
      title: 'Cards Per Row',
      description: 'Number of cards to display horizontally (1-6)',
      type: 'number',
      default: 6,
      minimum: 1,
      maximum: 6,
    },
    cardDisplayFields: {
      title: 'Card Display Fields',
      description:
        'Fields to display on cards. Common fields: format_category, description, views, fileSize, keywords, format_type, platform',
      type: 'array',
      items: {
        type: 'object',
        schema: cardDisplayFieldSchema,
      },
      default: [
        { '@id': 'field-1', field: 'format_category', label: 'Format' },
        { '@id': 'field-2', field: 'description', label: 'Description' },
      ],
      widget: 'object_list',
      schema: cardDisplayFieldSchema,
    },
    cardBadgeField: {
      title: 'Card Badge Field',
      description:
        'Field name to display as badge overlay on card thumbnail (e.g., status_text, event, fs__groundcontrol). Leave empty to hide badge.',
      type: 'string',
      default: 'format_type',
    },
    maxItemsToShow: {
      title: 'Max Items to Show',
      description: 'Maximum number of items to display',
      type: 'number',
      default: 12,
      minimum: 1,
      maximum: 48,
    },
    fetchDynamic: {
      title: 'Fetch Dynamic Data',
      description: 'Fetch featured items from Voyager API (tagged as featured)',
      type: 'boolean',
      default: true,
    },
    voyagerBaseUrl: {
      title: 'Voyager Base URL',
      description: 'Base URL for the Voyager search API',
      type: 'string',
      default: environment.voyager.baseUrl,
    },
    voyagerConfigId: {
      title: 'Voyager Config ID',
      description: 'Configuration ID for Voyager queries',
      type: 'string',
      default: environment.voyager.configId,
    },
    featuredQuery: {
      title: 'Featured Items Query',
      description:
        'Query string for featured items. Can be a simple filter (tag_tags:featured) or full URL params (f.grp_Geography.facet.sort=index&disp=D187992491DF&sort=bytes+desc). This same query is used for the "Show All" link.',
      type: 'string',
      default: 'tag_tags:featured',
    },
    searchResultsBaseUrl: {
      title: 'Search Results Base URL',
      description:
        'Base URL for search results links (e.g., https://ogc-sdi.voyagersearch.com/navigo/search)',
      type: 'string',
      default: environment.voyager.searchUrl,
    },
    detailsPageBaseUrl: {
      title: 'Details Page Base URL',
      description:
        'Base URL for item details page (e.g., https://ogc-sdi.voyagersearch.com/navigo/show or /details)',
      type: 'string',
      default: environment.voyager.detailsUrl,
    },
    showFormat: {
      title: 'Show Format',
      description: 'Display the data format label',
      type: 'boolean',
      default: true,
    },
    showShowAll: {
      title: 'Show "Show All" Link',
      description: 'Display "Show All" link at the bottom',
      type: 'boolean',
      default: true,
    },
    items: {
      title: 'Featured Items',
      description: 'Add and manage featured data items',
      type: 'array',
      items: {
        type: 'object',
        schema: featuredItemSchema,
      },
      default: [
        {
          id: '1',
          title: 'USGS Live Stream',
          description:
            'This service is available to all ArcGIS Online users with...',
          format: 'ArcGIS Online',
        },
        {
          id: '2',
          title: 'ArcGIS WebMap: 2',
          description: '',
          format: 'ArcGIS Online Application',
        },
        {
          id: '3',
          title: 'ArcGIS StoryMap:',
          description:
            'On Maui, wildlife, ecosystems, and residents are affected...',
          format: 'ArcGIS Online',
        },
        {
          id: '4',
          title: 'Guatemala Fire Pe',
          description: 'Dates of Imagery: 4/1/20 to 5/5/20',
          format: 'ArcGIS Online',
        },
        {
          id: '5',
          title: 'Drought Hazard Fi',
          description: '',
          format: 'WMS Layer',
        },
        {
          id: '6',
          title: 'County Wildland F',
          description: '',
          format: 'WMS Layer',
        },
      ],
      widget: 'object_list',
      schema: featuredItemSchema,
    },
  },
  required: [],
};

export default FeaturedBlockSchema;
